/**
 * `validateDoc` (architecture §11.2; ADR-0036, ADR-0040 decisions 8 and 15), on every write of a rich-text body:
 *
 *   1. byte size before parsing (256 KiB bodies, 64 KiB comments)
 *   2. `schema.nodeFromJSON(json)` then `node.check()` (unknown nodes and marks, invalid nesting rejected), after a
 *      bounded pre-walk (depth ≤ 20, ≤ 20,000 nodes) so hostile nesting never reaches recursive code
 *   3. per-node zod attribute schemas; unknown attributes are REJECTED (`validation`), not silently dropped
 *   4. `link.href`: https/http/mailto or a same-origin path, ≤ 2,048 chars, no credentials
 *   5. `image.fileId`: new references must satisfy the binding rule (§10.4), checked by the caller's ReferenceChecker
 *   6. references are validated against the diff with the stored document: references already present are preserved
 *      byte-for-byte whoever edits and never re-checked; a NEW user mention of someone who can't see the entity is
 *      stored inert; a NEW issue/project/document mention the author can't see becomes "Private issue|project|document"
 *   7. the canonical `node.toJSON()` is stored, never the client's bytes
 *
 * Steps 1–4 (`validateStructure`) run before the workspace lock; 5–7 (`resolveReferences`) inside it (§11.3).
 */
import type { Node as PMNode } from '@tiptap/pm/model';
import { fileRefs, mentionSet, walk, type DocJSON, type Reference } from './doc';
import { safeHref } from './links';
import { MARK_ATTRS, NODE_ATTRS, editorSchema } from './schema/index';
import { toPlainText } from './text';

export const BODY_MAX_BYTES = 256 * 1024;
export const COMMENT_MAX_BYTES = 64 * 1024;

export interface StructureOptions {
  /** 256 KiB for bodies, 64 KiB for comments. */
  maxBytes: number;
  maxDepth?: number;
  maxNodes?: number;
  /** The deployment's PUBLIC_URL (same-origin link paths). */
  publicUrl: string;
  /** Wire field name reported in errors (`contentData`, `bodyData`). */
  field?: string;
}

export interface DocValidationError {
  code: 'validation';
  message: string;
  field: string;
  /** JSON path of the offending node, for logs. */
  path?: string;
}

export type StructureResult = { ok: true; node: PMNode; json: DocJSON } | { ok: false; error: DocValidationError };

const NODE_KEYS = new Set(['type', 'attrs', 'content', 'marks', 'text']);
const encoder = new TextEncoder();

/** Steps 1–4: size, bounded walk, strict attributes, links, schema parse and `check()`. */
export function validateStructure(json: unknown, o: StructureOptions): StructureResult {
  const field = o.field ?? 'contentData';
  const fail = (message: string, path?: string): StructureResult => ({
    ok: false,
    error: { code: 'validation', message, field, ...(path !== undefined && { path }) },
  });
  let text: string;
  try {
    text = JSON.stringify(json) ?? '';
  } catch {
    return fail('The document is not valid JSON');
  }
  if (encoder.encode(text).length > o.maxBytes) return fail('The document is too large');
  const maxDepth = o.maxDepth ?? 20;
  const maxNodes = o.maxNodes ?? 20_000;
  if (!json || typeof json !== 'object' || (json as DocJSON).type !== 'doc') return fail('The document must be a doc node');
  let count = 0;
  const stack: { n: unknown; depth: number; path: string }[] = [{ n: json, depth: 0, path: '$' }];
  while (stack.length > 0) {
    const { n, depth, path } = stack.pop()!;
    if (++count > maxNodes) return fail('The document has too many nodes');
    if (depth > maxDepth) return fail('The document is nested too deeply', path);
    if (!n || typeof n !== 'object' || Array.isArray(n)) return fail('Invalid node', path);
    const node = n as Record<string, unknown>;
    for (const k of Object.keys(node)) if (!NODE_KEYS.has(k)) return fail(`Unknown node property ${k}`, path);
    const type = node.type;
    if (typeof type !== 'string' || !Object.hasOwn(NODE_ATTRS, type)) return fail(`Unknown node type`, path);
    if (node.attrs !== undefined) {
      const r = NODE_ATTRS[type]!.safeParse(node.attrs);
      if (!r.success) return fail(`Invalid ${type} attributes`, path);
    } else if (type === 'mention' || type === 'image' || type === 'heading') {
      return fail(`Missing ${type} attributes`, path);
    }
    if (type === 'text') {
      if (typeof node.text !== 'string' || node.text.length === 0) return fail('Empty text node', path);
      if (node.content !== undefined) return fail('A text node has no content', path);
    } else if (node.text !== undefined) {
      return fail('Only text nodes carry text', path);
    }
    if (node.marks !== undefined) {
      if (!Array.isArray(node.marks)) return fail('Invalid marks', path);
      for (const m of node.marks as unknown[]) {
        if (!m || typeof m !== 'object') return fail('Invalid mark', path);
        const mark = m as Record<string, unknown>;
        if (Object.keys(mark).some((k) => k !== 'type' && k !== 'attrs')) return fail('Unknown mark property', path);
        if (typeof mark.type !== 'string' || !Object.hasOwn(MARK_ATTRS, mark.type)) return fail('Unknown mark type', path);
        const r = MARK_ATTRS[mark.type]!.safeParse(mark.attrs ?? {});
        if (!r.success) return fail(`Invalid ${mark.type} attributes`, path);
        if (mark.type === 'link' && safeHref((mark.attrs as { href?: unknown } | undefined)?.href, o.publicUrl) === null) {
          return fail('That link is not allowed', path);
        }
      }
    }
    if (node.content !== undefined) {
      if (!Array.isArray(node.content)) return fail('Invalid content', path);
      const kids = node.content as unknown[];
      for (let i = kids.length - 1; i >= 0; i--) stack.push({ n: kids[i], depth: depth + 1, path: `${path}.content[${i}]` });
    }
  }
  let pm: PMNode;
  try {
    pm = editorSchema().nodeFromJSON(json);
    pm.check();
  } catch {
    return fail('The document structure is invalid');
  }
  return { ok: true, node: pm, json: pm.toJSON() as DocJSON };
}

/** What the server knows that the pure module doesn't (answered under the author's principal, inside the lock). */
export interface ReferenceChecker {
  /** Can this user see the containing entity? (`app.user_sees`) — false ⇒ the new chip is stored inert. */
  userCanSee(userId: string): boolean | Promise<boolean>;
  /** Can the author see the mentioned entity? (RLS read) — false ⇒ replaced by "Private …" text. */
  entityVisible(kind: 'issue' | 'project' | 'document', id: string): boolean | Promise<boolean>;
  /** The §10.4 binding rule for a NEW file reference by the author. */
  fileUsable(fileId: string): boolean | Promise<boolean>;
}

export interface ResolvedDoc {
  /** Canonical JSON to store. */
  doc: DocJSON;
  /** Derived plain text (search, previews): mentions as kind placeholders (§11.2). */
  plainText: string;
  /** Non-inert mentions of the stored result (diff with the previous set drives rows and notifications). */
  mentions: Reference[];
  files: string[];
  /** New references and what happened to them (for logs and tests). */
  changes: { inert: Reference[]; privatized: Reference[] };
}

const PRIVATE_TEXT: Readonly<Record<string, string>> = { issue: 'Private issue', project: 'Private project', document: 'Private document' };

function mentionKey(attrs: Record<string, unknown> | undefined): string {
  return `${String(attrs?.kind)}:${String(attrs?.id)}`;
}

/**
 * Steps 5–7 against the stored document (`null` for a new body): existing references keep their stored attributes
 * byte-for-byte; new ones are checked and made inert or private; new file references must be usable.
 */
export async function resolveReferences(
  structure: { json: DocJSON },
  stored: DocJSON | null,
  checker: ReferenceChecker,
  o: { field?: string } = {},
): Promise<{ ok: true; value: ResolvedDoc } | { ok: false; error: DocValidationError }> {
  const field = o.field ?? 'contentData';
  // stored multisets: mention attrs queued per (kind, id); file ids counted
  const storedMentions = new Map<string, Record<string, unknown>[]>();
  const storedFiles = new Map<string, number>();
  if (stored) {
    walk(stored, (n) => {
      if (n.type === 'mention') {
        const k = mentionKey(n.attrs);
        const q = storedMentions.get(k) ?? [];
        q.push({ ...(n.attrs ?? {}) });
        storedMentions.set(k, q);
      }
    });
    for (const f of fileRefs(stored)) storedFiles.set(f, (storedFiles.get(f) ?? 0) + 1);
  }
  const inert: Reference[] = [];
  const privatized: Reference[] = [];
  const doc = JSON.parse(JSON.stringify(structure.json)) as DocJSON;
  const userCache = new Map<string, boolean>();
  const entityCache = new Map<string, boolean>();

  const visitContent = async (node: DocJSON): Promise<DocValidationError | null> => {
    if (!node.content) return null;
    for (let i = 0; i < node.content.length; i++) {
      const child = node.content[i]!;
      if (child.type === 'mention') {
        const k = mentionKey(child.attrs);
        const q = storedMentions.get(k);
        if (q && q.length > 0) {
          child.attrs = q.shift()!; // preserved byte-for-byte, never re-checked
          continue;
        }
        const kind = child.attrs?.kind as Reference['kind'];
        const id = child.attrs?.id as string;
        if (kind === 'user') {
          let ok = userCache.get(id);
          if (ok === undefined) userCache.set(id, (ok = await checker.userCanSee(id)));
          child.attrs = { kind, id, inert: !ok };
          if (!ok) inert.push({ kind, id });
        } else {
          let ok = entityCache.get(k);
          if (ok === undefined) entityCache.set(k, (ok = await checker.entityVisible(kind, id)));
          if (!ok) {
            node.content[i] = { type: 'text', text: PRIVATE_TEXT[kind] ?? 'Private item', ...(child.marks && { marks: child.marks }) };
            privatized.push({ kind, id });
          }
        }
        continue;
      }
      if (child.type === 'image' || child.type === 'attachment') {
        const fileId = child.attrs?.fileId as string;
        const left = storedFiles.get(fileId) ?? 0;
        if (left > 0) {
          storedFiles.set(fileId, left - 1);
        } else if (!(await checker.fileUsable(fileId))) {
          return { code: 'validation', message: "That file can't be used here", field };
        }
      }
      const err = await visitContent(child);
      if (err) return err;
    }
    return null;
  };
  const err = await visitContent(doc);
  if (err) return { ok: false, error: err };
  // re-parse: replacing a mention by text can merge text nodes; store the canonical form
  let canonical: DocJSON;
  try {
    const pm = editorSchema().nodeFromJSON(doc);
    pm.check();
    canonical = pm.toJSON() as DocJSON;
  } catch {
    return { ok: false, error: { code: 'validation', message: 'The document structure is invalid', field } };
  }
  return {
    ok: true,
    value: {
      doc: canonical,
      plainText: toPlainText(canonical, PLACEHOLDER_LABELS),
      mentions: mentionSet(canonical),
      files: fileRefs(canonical),
      changes: { inert, privatized },
    },
  };
}

export interface ValidateDocOptions extends StructureOptions {
  stored: DocJSON | null;
  checker: ReferenceChecker;
}

/** Steps 1–7 in one call (tests, jobs); the pipeline calls the two halves around the lock. */
export async function validateDoc(
  json: unknown,
  o: ValidateDocOptions,
): Promise<{ ok: true; value: ResolvedDoc } | { ok: false; error: DocValidationError }> {
  const s = validateStructure(json, o);
  if (!s.ok) return s;
  return resolveReferences(s, o.stored, o.checker, o.field === undefined ? {} : { field: o.field });
}

/** `toPlainText` never asks for labels, but its signature requires a resolver (§11.2); this one answers nothing. */
const PLACEHOLDER_LABELS = { user: () => undefined, issue: () => undefined, project: () => undefined, document: () => undefined };
